import React from 'react'
import { CampaignGoalProps } from './CampaignGoal.types'
import { ActionCard } from 'components/cards/action-card'
import { useGetBusinessID } from '@marketing-milk/frontend'
import Select from 'react-select'
import {
  campaignObjectives,
  OutcomeCampaignObjective,
  optimizationGoalsForObjective,
  PixelEventType,
} from '@marketing-milk/interfaces'
import { useCustomConversions } from '../../../../../../hooks/campaigns/useCustomConversionsByBusiness'
import { capitalCase } from 'change-case'

// Sentinels rather than events anyone can optimize toward: the API returns them, but neither
// names something a pixel fires.
const unselectablePixelEvents = new Set([PixelEventType.FACEBOOK_SELECTED, PixelEventType.OTHER])

export function CampaignGoal({
  optimization,
  onSelectOptimization,
  pixelEventOrCCID,
  isLiveOnFacebook,
  onCustomizeGoalConfig,
}: CampaignGoalProps) {
  const businessID = useGetBusinessID()
  const { data: customConversions } = useCustomConversions(Number(businessID))

  const selectOptimization = (objective: OutcomeCampaignObjective) => {
    if (isLiveOnFacebook) return
    const goals = optimizationGoalsForObjective(objective)
    // the first entry is the one the team publishes most under that objective
    if (goals.length) onSelectOptimization(goals[0])
  }

  const performanceGoalOptions = optimization
    ? optimizationGoalsForObjective(optimization.objective).map(entry => ({
        label: entry.label,
        value: entry.key,
        entry,
      }))
    : []

  const pixelEventOptions = Object.values(PixelEventType)
    .filter(pixelEventType => !unselectablePixelEvents.has(pixelEventType))
    .map(pixelEventType => ({
      label: capitalCase(pixelEventType.toLowerCase().replaceAll('_', ' ')),
      value: pixelEventType as PixelEventType | number,
    }))
  const customConversionOptions =
    customConversions?.map(({ name, id, facebookCCIdentifier }) => ({
      label: `${name} (ID: ${facebookCCIdentifier})`,
      value: id as PixelEventType | number,
    })) ?? []

  return (
    <div className="row my-5" data-testid="campaign-optimizations">
      <div className="w-full">
        <div className="row">
          <div className="col-12 mb-2">
            <h5>Choose Your Campaign Objective</h5>
            <p>What Meta should deliver this campaign for.</p>
            {!optimization && (
              <div
                className="text-warning bg-warning faded px-3 py-2 rounded mt-2"
                data-testid="no-optimization-selected"
              >
                Nothing is selected yet. Pick an objective below — this campaign can't be published,
                and its creatives can't be filtered, until you do.
              </div>
            )}
            {isLiveOnFacebook && (
              <div className="text-warning bg-warning faded px-3 py-2 rounded mt-2">
                This campaign is already live on Facebook, so its delivery settings are locked.
                Duplicate the campaign to change delivery.
              </div>
            )}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5 px-2">
          {campaignObjectives.map(objectiveOption => {
            const selected = optimization?.objective === objectiveOption.objective
            return (
              <div
                key={objectiveOption.objective}
                style={{ display: 'grid', gridAutoFlow: 'column', gap: '5%' }}
              >
                <ActionCard
                  classes={`campaign-goal-opt-cards text-center mb-2 mb-md-4 ${
                    isLiveOnFacebook ? 'cursor-default' : ''
                  } ${selected ? 'selected' : ''}`}
                  active={selected}
                  onClick={() => selectOptimization(objectiveOption.objective)}
                >
                  <div
                    className={`p-2 h-100 center-vertical ${
                      isLiveOnFacebook ? 'text-inactive' : ''
                    }`}
                  >
                    <i className={`fad fa-${objectiveOption.icon} fa-3x mb-0`} />
                    <h5 className="mt-4">{objectiveOption.label}</h5>
                    <p>{objectiveOption.description}</p>
                  </div>
                </ActionCard>
              </div>
            )
          })}
        </div>

        {!!optimization && (
          <>
            <div className="row mt-2">
              <div className="col-12 mb-2">
                <h5>Select Performance Goal</h5>
              </div>
            </div>
            <div className="w-full grid grid-cols-2 py-4 border-b mt-2 items-center">
              <div>
                <p className="text-lg font-medium text-gray-600 mb-1">Performance Goal</p>
                <p className="text-sm text-gray-500">{optimization.description}</p>
              </div>
              <Select
                isDisabled={isLiveOnFacebook}
                placeholder="Select what to optimize delivery for"
                value={performanceGoalOptions.find(_ => _.value === optimization.key)}
                options={performanceGoalOptions}
                onChange={(e: any) => !!e?.entry && onSelectOptimization(e.entry)}
              />
            </div>
          </>
        )}

        {optimization?.promotedObjectKind === 'web-event' && (
          <>
            <div className="row mt-2">
              <div className="col-12 mb-2">
                <h5>Select Web Conversion</h5>
              </div>
            </div>
            <div className="w-full grid grid-cols-2 py-4 border-b mt-2">
              <p className="text-lg font-medium text-gray-600">Conversion Event</p>
              <Select
                isDisabled={isLiveOnFacebook}
                placeholder="Select Web Event to optimize for"
                value={
                  pixelEventOptions.find(_ => _.value === pixelEventOrCCID) ??
                  customConversionOptions.find(_ => _.value === pixelEventOrCCID)
                }
                options={[
                  {
                    label: 'Custom Conversions',
                    options: customConversionOptions,
                  },
                  {
                    label: 'Pixel Events',
                    options: pixelEventOptions,
                  },
                ]}
                onChange={(e: any) =>
                  !!e?.value && !!onCustomizeGoalConfig && onCustomizeGoalConfig(e.value)
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
